import { useMemo } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { humanizeSupabaseError } from "@/lib/errors";
import { supabase } from "@/integrations/supabase/client";
import { runOrQueue } from "@/features/offline/queue";
import { useStaff } from "@/hooks/useStaff";

/**
 * Staff-facing activation metrics — read definitions + entries, and
 * record new entries with the caller's staff_id.
 *
 * Mirrors the admin metrics module but with two key differences:
 *   1. Mutations are insert-only — no edit, no delete. Cleanup stays
 *      an admin responsibility.
 *   2. Reads are gated by `can_view_activation()` via RLS, so staff
 *      only see metrics for activations they're rostered on.
 */

export interface StaffMetricDefinition {
  id: string;
  activation_id: string;
  metric_name: string;
  sort_order: number;
  prompt_at_checkout: boolean;
  created_at: string;
}

/** A metric the rep should be prompted for at check-out. */
export interface CheckoutMetric {
  id: string;
  metric_name: string;
}

export interface StaffMetricEntry {
  id: string;
  definition_id: string;
  activation_id: string;
  value: number;
  entry_date: string;
  notes: string | null;
  staff_id: string | null;
  created_at: string;
}

export interface StaffMetricSummary {
  definition: StaffMetricDefinition;
  total: number;
  entryCount: number;
  myContribution: number;
}

export const staffMetricsKeys = {
  all: ["staff", "metrics"] as const,
  byActivation: (activationId: string | null | undefined) =>
    [...staffMetricsKeys.all, activationId ?? null] as const,
};

export function useStaffActivationMetrics(activationId: string | undefined) {
  const { data: staff } = useStaff();
  const myStaffId = staff?.id ?? null;

  return useQuery({
    queryKey: staffMetricsKeys.byActivation(activationId),
    enabled: Boolean(activationId),
    queryFn: async (): Promise<{
      definitions: StaffMetricDefinition[];
      entries: StaffMetricEntry[];
      summaries: StaffMetricSummary[];
    }> => {
      const [defsRes, entriesRes] = await Promise.all([
        supabase
          .from("activation_metric_definitions")
          .select("id, activation_id, metric_name, sort_order, prompt_at_checkout, created_at")
          .eq("activation_id", activationId!)
          .order("sort_order", { ascending: true }),
        supabase
          .from("activation_metric_entries")
          .select(
            "id, definition_id, activation_id, value, entry_date, notes, staff_id, created_at",
          )
          .eq("activation_id", activationId!)
          .order("created_at", { ascending: false }),
      ]);
      if (defsRes.error) throw defsRes.error;
      if (entriesRes.error) throw entriesRes.error;

      const definitions = (defsRes.data ?? []) as StaffMetricDefinition[];
      const entries = ((entriesRes.data ?? []) as StaffMetricEntry[]).map(
        (e) => ({ ...e, value: Number(e.value) }),
      );

      // Build per-metric summaries client-side. Tiny dataset, no point
      // round-tripping aggregations to the server.
      const totals = new Map<
        string,
        { total: number; count: number; mine: number }
      >();
      for (const e of entries) {
        const cur = totals.get(e.definition_id) ?? {
          total: 0,
          count: 0,
          mine: 0,
        };
        cur.total += e.value;
        cur.count += 1;
        if (myStaffId && e.staff_id === myStaffId) cur.mine += e.value;
        totals.set(e.definition_id, cur);
      }
      const summaries: StaffMetricSummary[] = definitions.map((def) => {
        const t = totals.get(def.id) ?? { total: 0, count: 0, mine: 0 };
        return {
          definition: def,
          total: t.total,
          entryCount: t.count,
          myContribution: t.mine,
        };
      });

      return { definitions, entries, summaries };
    },
  });
}

/**
 * Record a value against a metric. Optimistic — adds to the local
 * total instantly, then rolls back on failure. Defaults to value=1
 * for the tap-to-increment pattern; pass a different number for
 * batch entries.
 */
export function useRecordMetricEntryMutation() {
  const qc = useQueryClient();
  const { data: staff } = useStaff();

  return useMutation({
    // Run even when offline — otherwise React Query pauses the mutation and the
    // offline queue (inside the mutationFn) never gets a chance to capture it.
    networkMode: "always",
    mutationFn: async ({
      definitionId,
      activationId,
      value = 1,
    }: {
      definitionId: string;
      activationId: string;
      value?: number;
    }) => {
      if (!staff?.id) {
        throw new Error(
          "You don't appear to be linked to a staff record. Please reload and try again.",
        );
      }
      // entry_date is the calendar day in the user's local TZ, captured now —
      // at the moment of the tap — so an entry made offline keeps its real
      // date even if it syncs after midnight.
      const today = (() => {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      })();

      // Online: writes through immediately. Offline / flaky: persists to the
      // IndexedDB queue and replays on reconnect. Throws only on a permanent
      // (non-retryable) error, which the optimistic rollback below handles.
      return runOrQueue("metric_entry", {
        definitionId,
        activationId,
        value,
        entryDate: today,
        staffId: staff.id,
      });
    },
    onMutate: async ({ definitionId, activationId, value = 1 }) => {
      // Optimistic bump of the local summary so the big number
      // increments instantly.
      await qc.cancelQueries({
        queryKey: staffMetricsKeys.byActivation(activationId),
      });
      const previous = qc.getQueryData<{
        definitions: StaffMetricDefinition[];
        entries: StaffMetricEntry[];
        summaries: StaffMetricSummary[];
      }>(staffMetricsKeys.byActivation(activationId));
      if (previous) {
        const nextSummaries = previous.summaries.map((s) =>
          s.definition.id === definitionId
            ? {
                ...s,
                total: s.total + value,
                entryCount: s.entryCount + 1,
                myContribution: s.myContribution + value,
              }
            : s,
        );
        qc.setQueryData(staffMetricsKeys.byActivation(activationId), {
          ...previous,
          summaries: nextSummaries,
        });
      }
      return { previous };
    },
    onError: (err, { activationId }, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(
          staffMetricsKeys.byActivation(activationId),
          ctx.previous,
        );
      }
      toast.error(
        humanizeSupabaseError(err, "Couldn't record entry."),
      );
    },
    onSuccess: (result) => {
      if (result.queued) {
        toast.success("Saved offline — syncs when you reconnect.");
      }
    },
    onSettled: (result, _err, { activationId }) => {
      // Keep the optimistic bump while a write waits in the offline queue —
      // invalidating now would refetch server state that doesn't include it
      // yet. The queue invalidates these keys itself once the write syncs.
      if (result && !result.queued) {
        qc.invalidateQueries({
          queryKey: staffMetricsKeys.byActivation(activationId),
        });
        // Nudge the admin cache too so the admin Metrics tab refreshes
        // on next focus / refetch — same pattern as the tasks toggle.
        qc.invalidateQueries({
          queryKey: ["admin", "metrics", "activation", activationId],
        });
      }
    },
  });
}

/**
 * The metrics a rep should be asked for at check-out for this activation.
 * Kept as its own small query so the check-out review dialog can prompt for
 * them; a healthy staleTime means it stays warm from an earlier online load
 * and still resolves if the rep is offline at shift end (they simply don't
 * get prompted if it was never loaded — check-out is never blocked either way).
 */
export function useCheckoutMetrics(activationId: string | null | undefined) {
  return useQuery({
    queryKey: [...staffMetricsKeys.byActivation(activationId), "checkout"],
    enabled: Boolean(activationId),
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<CheckoutMetric[]> => {
      const { data, error } = await supabase
        .from("activation_metric_definitions")
        .select("id, metric_name")
        .eq("activation_id", activationId!)
        .eq("prompt_at_checkout", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CheckoutMetric[];
    },
  });
}

/**
 * Convenience: aggregate "have any metrics been recorded today" —
 * used to decide whether to render the section header's day pill.
 * Lightweight — derives from the cached summaries.
 */
export function useHasMetricActivity(
  summaries: StaffMetricSummary[] | undefined,
): boolean {
  return useMemo(
    () => (summaries ?? []).some((s) => s.total > 0),
    [summaries],
  );
}
